import { VatCode } from '@constants'
import { useBusinessContext } from '@context'
import { ContactType, fetchContact, IBlueprint, IContact } from '@query'
import { useQuery } from '@tanstack/react-query'

const EU_SALE_VAT_CODES = new Set<number>([VatCode.EU_SALE_OF_GOODS, VatCode.EU_SALE_OF_SERVICES])

const blueprintEntries = (blueprint?: IBlueprint) => [
  ...(blueprint?.debet_entries ?? []),
  ...(blueprint?.credit_entries ?? []),
  ...(blueprint?.expense_entries ?? [])
]

export const blueprintHasEuSaleVatCode = (blueprint?: IBlueprint): boolean =>
  blueprintEntries(blueprint).some(
    entry => entry.vat_code != null && EU_SALE_VAT_CODES.has(entry.vat_code)
  )

export type EuSaleWarning = 'missingContact' | 'missingVatId' | 'privatePerson' | null

export const getEuSaleWarning = ({
  hasEuSale,
  contact,
  contactLoading
}: {
  hasEuSale: boolean
  contact: IContact | null | undefined
  contactLoading: boolean
}): EuSaleWarning => {
  if (!hasEuSale || contactLoading) return null
  if (!contact) return 'missingContact'
  if (contact.type === ContactType.PERSON) return 'privatePerson'
  if (!contact.invoicing_tax_code?.trim()) return 'missingVatId'
  return null
}

export const useEuSaleWarning = (
  contactId: number | null | undefined,
  blueprint?: IBlueprint
): EuSaleWarning => {
  const { slug } = useBusinessContext()
  const hasEuSale = blueprintHasEuSaleVatCode(blueprint)

  const { data: contact, isFetching: isContactFetching } = useQuery({
    queryKey: [slug, 'contacts', contactId],
    queryFn: () => fetchContact({ slug, contactId: contactId as number }),
    enabled: Boolean(contactId)
  })
  const contactLoading = Boolean(contactId) && (isContactFetching || !contact)

  return getEuSaleWarning({
    hasEuSale,
    contact: contactId ? contact : null,
    contactLoading
  })
}
